import { Injectable, signal, inject } from '@angular/core';
import { OrdersService } from '../../features/owner/orders/orders.service';
import { BillResponse } from '../../features/owner/orders/order.model';
import { forkJoin, map, catchError, of } from 'rxjs';
import { ApiService } from './api.service';
import { EscPosService } from './esc-pos.service';
import { ApplicationRef } from '@angular/core';
import { NotificationService } from './notification.service';

export interface PrintData {
  bill: BillResponse;
  cafeAddress: string;
  receiptFooter: string;
  logoUrl?: string;
  receiptCodeType?: 'BARCODE' | 'QRCODE' | 'NONE';
  printKitchenTicket?: boolean;
  printLogoOnBack?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PrintService {
  private ordersService = inject(OrdersService);
  private api = inject(ApiService);
  private escPosService = inject(EscPosService);
  private appRef = inject(ApplicationRef);
  private notif = inject(NotificationService);

  // Signal to hold the current bill data that needs printing
  public currentPrintData = signal<PrintData | null>(null);

  /**
   * Triggers the silent printing workflow.
   * Fetches the bill and cafe info, and attempts to print silently via WebSerial ESC/POS.
   * If not connected, falls back to window.print().
   */
  public triggerPrint(orderId: string): void {
    forkJoin({
      bill: this.ordersService.generateBill(orderId),
      cafeInfo: this.api.get<any>('/owner/cafe').pipe(
        map(res => res.data),
        catchError(() => of(null))
      )
    }).subscribe({
      next: async ({ bill, cafeInfo }) => {
          const printData: PrintData = {
            bill,
            cafeAddress: [cafeInfo?.addressLine1, cafeInfo?.city, cafeInfo?.state].filter(Boolean).join(', ') || 'Gourmet Coffee & Snacks',
            receiptFooter: cafeInfo?.receiptFooter || 'Thank you for visiting!',
            logoUrl: cafeInfo?.logoUrl || null,
            receiptCodeType: cafeInfo?.receiptCodeType || 'BARCODE',
            printKitchenTicket: cafeInfo?.printKitchenTicket || false,
            printLogoOnBack: cafeInfo?.printLogoOnBack || false
          };

          // Compute percentages if the backend did not provide them
          if (!printData.bill.discountPercentage && printData.bill.discountAmount && printData.bill.subtotal) {
            printData.bill.discountPercentage = Math.round((printData.bill.discountAmount / printData.bill.subtotal) * 100);
          }
          if (!printData.bill.taxPercentage && printData.bill.taxAmount && printData.bill.subtotal) {
            const taxableAmount = printData.bill.subtotal - (printData.bill.discountAmount || 0);
            if (taxableAmount > 0) {
              printData.bill.taxPercentage = Math.round((printData.bill.taxAmount / taxableAmount) * 100);
            }
          }
          if (!printData.bill.serviceChargePercentage && printData.bill.serviceCharge && printData.bill.subtotal) {
            printData.bill.serviceChargePercentage = Math.round((printData.bill.serviceCharge / printData.bill.subtotal) * 100);
          }
          if (!printData.bill.deliveryChargePercentage && printData.bill.deliveryCharge && printData.bill.subtotal) {
            printData.bill.deliveryChargePercentage = Math.round((printData.bill.deliveryCharge / printData.bill.subtotal) * 100);
          }

        if (this.escPosService.isConnected()) {
          if (printData.printKitchenTicket) {
            await this.escPosService.printKitchenTicket(printData);
          }
          const success = await this.escPosService.printReceipt(printData, false);
          if (success) {
            console.log('Printed successfully via WebSerial');
            return;
          }
        }
        
        // Fallback to window.print()
        console.warn('WebSerial printer not connected or failed. Falling back to window.print()');
        this.currentPrintData.set(printData);
        
        // Force Angular to render the receipt synchronously
        this.appRef.tick();

        try {
          window.print();
        } catch (e) {
          console.error('Print failed or was blocked by browser', e);
          this.notif.error('Print Blocked', 'The browser blocked the print dialog. Please allow popups/printing for this site.');
        } finally {
          // Clear the receipt data from memory after a delay so it has time to spool
          setTimeout(() => {
            this.currentPrintData.set(null);
            this.appRef.tick();
          }, 1500);
        }
      },
      error: (err) => {
        console.error('Failed to prepare receipt for printing', err);
      }
    });
  }
}
